import { useAuthenticationStore } from '@cplug/frontend-shared-domains/domain/authentication'
import { storeToRefs } from 'pinia'
import { navigateTo, useRuntimeConfig } from '#app'

import type { ExtendedCPlugAPI } from '~/global/types/runtime-config'
import { useLegacyBridgeToken } from '~/shared/composables/useLegacyBridgeToken'

function mapHubPathToErpPlansPath (hubPath: string): string {
  if (hubPath === '/' || hubPath === '') {
    return '/dashboard/plans/my-plan'
  }

  const normalized = hubPath.startsWith('/') ? hubPath : `/${hubPath}`

  if (normalized.startsWith('/dashboard/plans/')) {
    return normalized
  }

  if (normalized.startsWith('/plans/')) {
    return `/dashboard${normalized}`
  }

  return '/dashboard/plans/my-plan'
}

function isCrossOriginHub (hubUrl: string): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    return new URL(hubUrl).origin !== window.location.origin
  } catch {
    return true
  }
}

function buildSubscriptionHubUrl (
  hubUrl: string,
  path: string,
  companyId: number,
  tokens?: { accessToken: string, refreshToken: string }
): string {
  const url = new URL(path, hubUrl)
  url.searchParams.set('setup_company_id', companyId.toString())

  if (tokens != null) {
    if (tokens.accessToken.length > 0) {
      url.searchParams.set('access_token', tokens.accessToken)
    }
    if (tokens.refreshToken.length > 0) {
      url.searchParams.set('refresh_token', tokens.refreshToken)
    }
  }

  return url.toString()
}

function assignSubscriptionHubLocation (hubWindow: Window | null, url: string): void {
  if (hubWindow != null && !hubWindow.closed) {
    hubWindow.location.replace(url)

    return
  }

  window.open(url, '_blank')
}

export async function resolveHubAuthTokens (
  cplugAPI: ExtendedCPlugAPI,
  companyId: number,
  bearerToken: string,
  bearerRefreshToken: string,
  hubUrl: string
): Promise<{ accessToken: string, refreshToken: string } | undefined> {
  const crossOrigin = isCrossOriginHub(hubUrl)

  if (!crossOrigin && cplugAPI.withCredentials) {
    return undefined
  }

  if (crossOrigin && cplugAPI.withCredentials) {
    const { resolveLegacyBridgeTokens } = useLegacyBridgeToken()
    const bridgeTokens = await resolveLegacyBridgeTokens(
      cplugAPI.baseURL,
      companyId,
      true,
      bearerToken,
      bearerRefreshToken
    )

    if (bridgeTokens != null && bridgeTokens.refreshToken.length > 0) {
      return bridgeTokens
    }

    if (bearerRefreshToken.length > 0) {
      return { accessToken: bearerToken, refreshToken: bearerRefreshToken }
    }

    if (bridgeTokens != null && (bridgeTokens.accessToken.length > 0 || bridgeTokens.refreshToken.length > 0)) {
      return bridgeTokens
    }

    return undefined
  }

  if (bearerToken.length > 0 || bearerRefreshToken.length > 0) {
    return { accessToken: bearerToken, refreshToken: bearerRefreshToken }
  }

  return undefined
}

export function useRedirectToSubscriptionHub (path = '/'): void {
  const config = useRuntimeConfig()
  const cplugAPI = config.public.cplugAPI as ExtendedCPlugAPI
  const authStore = useAuthenticationStore()
  const { companies, token, refreshToken } = storeToRefs(authStore)

  const company = companies.value[0]
  if (!company?.id) {
    return
  }

  const hubUrl = config.public.subscriptionHubURL as string
  if (!hubUrl) {
    navigateTo(mapHubPathToErpPlansPath(path))
    return
  }

  const hubWindow = window.open('about:blank', '_blank')

  resolveHubAuthTokens(
    cplugAPI,
    company.id!,
    token.value,
    refreshToken.value,
    hubUrl
  ).then((tokens) => {
    assignSubscriptionHubLocation(
      hubWindow,
      buildSubscriptionHubUrl(hubUrl, path, company.id!, tokens)
    )
  })
}
